import { ILayer } from '../../../../models';
import {
  createUnknownLayerStatus,
  getLayerKey,
  ILayerEventSubscription,
  ILayerHealthSnapshot,
  ILayerRuntimeStatus,
  LayerHealthChanged,
  LayerHealthStatus
} from './LayerHealthTypes';

export default class LayerHealthManager {
  private readonly onChanged: LayerHealthChanged;
  private layerStatusByKey: { [key: string]: ILayerRuntimeStatus };
  private layerStatusRevision: number;
  private layerStatusCycle: number;
  private layerEventSubscriptions: ILayerEventSubscription[];
  private subscribedLayerInstances: WeakSet<any>;

  constructor(layers: ILayer[], onChanged: LayerHealthChanged) {
    this.onChanged = onChanged;
    this.layerStatusByKey = this.createInitialLayerStatus(layers);
    this.layerStatusRevision = 0;
    this.layerStatusCycle = 0;
    this.layerEventSubscriptions = [];
    this.subscribedLayerInstances = new WeakSet<any>();
  }

  public getSnapshot(): ILayerHealthSnapshot {
    return {
      layerStatusByKey: this.layerStatusByKey,
      layerStatusRevision: this.layerStatusRevision,
      layerStatusCycle: this.layerStatusCycle
    };
  }

  public getLayerStatus(layer?: ILayer): ILayerRuntimeStatus {
    const key = getLayerKey(layer);

    return this.layerStatusByKey[key] || createUnknownLayerStatus(this.layerStatusCycle);
  }

  public getLayerKey(layer?: ILayer): string {
    return getLayerKey(layer);
  }

  public nextCycle(): void {
    this.layerStatusRevision++;
    this.layerStatusCycle++;
    this.emit();
  }

  public setUnknown(layer?: ILayer): void {
    this.setLayerStatus(layer, 'unknown');
  }

  public setLayerStatus(layer: ILayer | undefined, status: LayerHealthStatus, lastError?: string): void {
    if (!layer) {
      return;
    }

    const layerKey = getLayerKey(layer);
    const current = this.layerStatusByKey[layerKey];
    const normalizedError = status === 'warning' ? lastError : undefined;

    // Keep warning sticky within the same render cycle.
    if (status === 'ok' && current && current.status === 'warning' && current.cycle === this.layerStatusCycle) {
      return;
    }

    if (current && current.status === status && current.lastError === normalizedError) {
      return;
    }

    this.layerStatusRevision++;
    this.layerStatusByKey = {
      ...this.layerStatusByKey,
      [layerKey]: {
        status,
        lastError: normalizedError,
        lastUpdated: Date.now(),
        cycle: this.layerStatusCycle
      }
    };

    this.emit();
  }

  public registerLayerEvents(layer: ILayer, layerRef: any): void {
    const leafletLayer = layerRef?.leafletElement;

    if (!leafletLayer || this.subscribedLayerInstances.has(leafletLayer)) {
      return;
    }

    const onLoad = () => this.setLayerStatus(layer, 'ok');
    const onTileLoad = () => this.setLayerStatus(layer, 'ok');
    const onError = (errorEvent: any) => this.setLayerStatus(layer, 'warning', this.getTileLayerErrorMessage(errorEvent));

    leafletLayer.on('load', onLoad);
    leafletLayer.on('tileload', onTileLoad);
    leafletLayer.on('tileerror', onError);

    this.layerEventSubscriptions.push({
      layer: leafletLayer,
      onLoad,
      onTileLoad,
      onError
    });
    this.subscribedLayerInstances.add(leafletLayer);
  }

  public dispose(): void {
    this.layerEventSubscriptions.forEach(subscription => {
      subscription.layer.off('load', subscription.onLoad);
      subscription.layer.off('tileload', subscription.onTileLoad);
      subscription.layer.off('tileerror', subscription.onError);
    });

    this.layerEventSubscriptions = [];
    this.subscribedLayerInstances = new WeakSet<any>();
  }

  private createInitialLayerStatus(layers: ILayer[]): { [key: string]: ILayerRuntimeStatus } {
    const statusMap = {} as { [key: string]: ILayerRuntimeStatus };

    layers.forEach(layer => {
      statusMap[getLayerKey(layer)] = createUnknownLayerStatus(0);
    });

    return statusMap;
  }

  private getTileLayerErrorMessage(errorEvent: any): string {
    if (errorEvent?.error?.message) {
      return errorEvent.error.message;
    }

    if (errorEvent?.error?.statusText) {
      return errorEvent.error.statusText;
    }

    return 'Kunne ikke hente data fra ekstern korttjeneste';
  }

  private emit(): void {
    this.onChanged(this.getSnapshot());
  }
}
